import React, { useState, useEffect, useCallback } from "react";
import { Modal, Button, IconButton, Tooltip, Collapse, Typography, Card, CardHeader, CardContent, CardActions, Box, Menu, MenuItem, ListItemIcon, Radio } from '@mui/material'
import { useResizeDetector } from 'react-resize-detector'
import SettingsIcon from '@mui/icons-material/Settings';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import BarChartIcon from '@mui/icons-material/BarChart';
import ScatterPlotIcon from '@mui/icons-material/ScatterPlot';
import GridOnIcon from '@mui/icons-material/GridOn'; // For Heatmap chart type
import BubbleChartOutlinedIcon from '@mui/icons-material/BubbleChartOutlined'; // For clustering
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import TuneIcon from '@mui/icons-material/Tune';
import InsightsIcon from '@mui/icons-material/Insights';
import { AddColumnIcon, RemoveColumnIcon, XAxisIcon, YAxisIcon, MoveCursorIcon, BoxAndWhiskerIcon, DeltaIcon, Log2Icon, ACTIVE_BLUE, INACTIVE_GREY } from "../shared/utilities/CustomIcons";
import './ResponsiveCard.css'
import { debounce, filter } from 'lodash';
import CircularProgressWithLabel from "../shared/components/CircularProgressWithLabel";
import CustomFilterModal from "../shared/components/CustomFilterModal";
import { BubbleChart, SettingsInputAntennaTwoTone, SettingsRemoteSharp, SettingsSystemDaydreamSharp } from "@mui/icons-material";
import { boolean, parse } from "mathjs";
import { selectedGridRowsCountSelector } from "@mui/x-data-grid";

interface IResponsiveCardProps {
  cardType: string
  id: number
  name?: string
  subheader?: string
  edit?: boolean
  deletable?: boolean
  refresh?: boolean
  filter?: boolean
  initialWidth?: number
  initialHeight?: number
  message?: string
  children: (dims: { width: number; height: number; editMode: boolean }) => React.ReactNode;
  className?: any
  hoverHighlight?: boolean
  selected?: boolean;
  onClick?: () => void 
  onDelete?: (id: number, type: string) => void
  onEdit?: (newContent: string | number[]) => void;
  isEditing?: boolean,
  isLoading?: boolean,
  loadingProgress?: number,
  alerts?: React.ReactNode,
  numColumns?: number;
  tileModeOn?: boolean;
  onAddColumn?: () => void;
  onRemoveColumn?: () => void;
  tileSharedY?: boolean; 
  setTileSharedY?: (tileSharedY: boolean) => void;
  tileSharedX?: boolean; 
  setTileSharedX?: (tileSharedX: boolean) => void;
  setPlotConfigs?: (config: { [key: string]: PlotConfig }, save: boolean) => void;
  plotConfig?: PlotConfig;
  selectedItems?: SelectedItems;
  setItems?: (newSelectedItems: SelectedItems) => void;  
  openPlotOptionsModal?: () => void;
  setSelectedMultiPlot?: () => void;
  categoricalColumns?: any;
  numericColumns?: any;
  updateFilters?: () => void;
  axisFiltersState?: FilterState;
  setAxisFiltersState?: React.Dispatch<React.SetStateAction<FilterState>>;
  setConfigureTab?: () => void;
  handleDownloadData?: () => void;
  setDataPipeline?: (id: number) => void;
}

const style = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
};

const ResponsiveCard: React.FC<IResponsiveCardProps> = ({
  cardType,
  id,
  name,
  subheader,
  edit,
  deletable,
  refresh,
  filter,
  initialWidth,
  initialHeight,
  message,
  children,
  className,
  hoverHighlight,
  selected,
  onClick,
  onDelete,
  onEdit,
  isEditing,
  isLoading,
  loadingProgress,
  alerts,
  numColumns,
  tileModeOn,
  onAddColumn,
  onRemoveColumn,
  tileSharedY,
  setTileSharedY,
  tileSharedX,
  setTileSharedX,
  setPlotConfigs,
  plotConfig,
  selectedItems,
  setItems,
  openPlotOptionsModal,
  setSelectedMultiPlot,
  categoricalColumns,
  numericColumns,
  updateFilters,
  axisFiltersState,
  setAxisFiltersState,
  setConfigureTab,
  handleDownloadData,
  setDataPipeline,
}) => {  

  // List of quick control data selection columns to select
  // TO DO: dynamically query API to determine pipeline ids that match desired column
  const geneExpressionDataSelectionOptions = [
    { label: 'Raw', value: 'Raw_Count', pipeline: null },
    { label: 'Norm Count (HK)', value: 'Norm_Count', pipeline: 4}, 
    { label: 'Norm Count (Pos Ctrl + Input Vol)', value: 'Norm_Count (Input Volume + Positive Control)', pipeline: 6},
    { label: 'Norm Count (Input Vol)', value: 'Norm_Count (Input Volume)', pipeline: 5},
    { label: 'Norm Count (Positive Control)', value: 'Norm_Count (Positive Control)', pipeline: 7},
    { label: 'Norm Count Within (HK)', value: 'Norm_Count Within (HK)', pipeline: 8},
  ];


  const [expanded, setExpanded] = React.useState(false);
  const [showModal, setShowModal] = React.useState(false);
  const [editMode, setEditMode] = React.useState(false);
  const [title, setTitle] = useState(name || '');
  const { ref, width, height } = useResizeDetector();
  const [customFilterModalOpen, setCustomFilterModalOpen] = useState<boolean>(false);
  const [filterValue, setFilterValue] = useState<FilterState>({});
  const [lastPlotType, setLastPlotType] = useState<string | undefined>(undefined);

  const [initialConfig, setInitialConfig] = useState<PlotConfig | undefined>(plotConfig);
  const [lastSelectedItems, setLastSelectedItems] = useState<SelectedItems | undefined>(undefined);
  const [hasReferences, setHasReferences] = useState<boolean>(false); // if data being plotted has relative data references specified
  const [differentialModeEnabled, setDifferentialModeEnabled] = useState<boolean>(false);

  const [showOptions, setShowOptions] = useState(true);
  const [anchorDataSelectionMenu, setAnchorDataSelectionMenu] = React.useState<null | HTMLElement>(null);
  const [anchorControlSelectionMenu, setAnchorControlSelectionMenu] = React.useState<null | HTMLElement>(null);
  const [anchorDiffControlSelectionMenu, setAnchorDiffControlSelectionMenu] = React.useState<null | HTMLElement>(null);
  
  const openDataSelection = Boolean(anchorDataSelectionMenu);
  const [selectedDataSelection, setSelectedDataSelection] = useState(geneExpressionDataSelectionOptions[0]?.value); // default value

  const openControlSelection = Boolean(anchorControlSelectionMenu);
  const [selectedControlSelection, setSelectedControlSelection] = useState<string | undefined>(undefined);

  const openDiffControlSelection = Boolean(anchorDiffControlSelectionMenu);
  const [selectedDiffControlSelection, setSelectedDiffControlSelection] = useState<string | undefined>(undefined);

  const [diffReferenceOptions, setDiffReferenceOptions] = useState<string[] | undefined>([]);

  const [isGeneExpressionData, setIsGeneExpressionData] = useState(false);

  const pvalueCTRLenabled = false;
  const diffModeREFenabled = false;

 
  // const [cardDimensions, setCardDimensions] = useState({ width, height });
  // console.log("selectedItems: ", selectedItems);

  // set default selectedDataSelection based on the selectedItems
  useEffect(() => {
    if (selectedItems) {
      const axisData = plotConfig?.plotType === 'heatmap' ? selectedItems['Z-Axis Data']?.[0] : selectedItems['Y-Axis Data']?.[0];
      if (axisData) {
        const currentSelection = geneExpressionDataSelectionOptions.find(option => option.value === axisData.values[0]);
        if (currentSelection) {
          // only set it if it's different and not already set
          if (currentSelection.value !== selectedDataSelection) {
            setSelectedDataSelection(currentSelection.value);
          }
        }
        // set differentialModeEnabled based on processingMode
        if (axisData.processingMode === 'relative') {
          setDifferentialModeEnabled(true);
        } else {
          setDifferentialModeEnabled(false);
        }
      }
    }
  }, [selectedItems, isLoading]);

  // // set default control for pvalue calc
  useEffect(() => {
    if (plotConfig) {
      const control = plotConfig?.pvalueControl;
      if (control) {
        // only set if it's different and not already set
        if (control !== selectedControlSelection) {
          setSelectedControlSelection(control);
        }
      }
    }
  }, [plotConfig, selectedControlSelection]);

  useEffect(() => {
    let axisData: AxisItem | undefined = undefined;
    if (plotConfig?.plotType === 'heatmap') {
      axisData = selectedItems?.['Z-Axis Data']?.[0];
    } else {
      axisData = selectedItems?.['Y-Axis Data']?.[0];
    }
    const references = axisData?.references;
    setHasReferences(Array.isArray(references) && references.length > 0);

    if (
      Array.isArray(references) &&
      references.length === 1 &&
      references[0]?.column?.label === 'Treatment_and_Dose' &&
      categoricalColumns?.['Treatment_and_Dose']
    ) {
      setDiffReferenceOptions(categoricalColumns['Treatment_and_Dose']);
    }


    if (selectedItems?.['Y-Axis Data']?.[0]?.versionType === 3 || selectedItems?.['Z-Axis Data']?.[0]?.versionType === 3 ) {
      setIsGeneExpressionData(true);
    } else {
      setIsGeneExpressionData(false);
    }
    
  },[selectedItems, plotConfig]);

  useEffect(()=> {
    if (axisFiltersState !== undefined){
      // console.log("Just updated axisFiltersState: ")
      const filtersToAdd = []
      for (let k of Object.keys(axisFiltersState)){
        const key = parseInt(k) as number;
        // let filterValue:Filter = axisFiltersState[key].filter.value
        // let split = key.split('|')
        // const version = split[2]
        // const viewable = !!split[1]
        // const col = split[3]
        // const axis = split[0][0].toLowerCase() as any
        

        const filterOption:FilterOption = {
          viewable: axisFiltersState[key].viewable,
          value: axisFiltersState[key].versionType + '|' + axisFiltersState[key].column,
          // axis:axis
        }
        filtersToAdd.push(filterOption);
      }
      // setFilterValue(filtersToAdd);
    }
  }, [axisFiltersState]);

  // State to store the last valid (non-zero) width and height
  const [lastValidWidth, setLastValidWidth] = useState(initialWidth || 0);
  const [lastValidHeight, setLastValidHeight] = useState(initialHeight || 0);

  // Initialize cardDimensions using the last valid width and height if width/height is undefined
  const [cardDimensions, setCardDimensions] = useState({
    width: width !== undefined && width > 0 ? width : lastValidWidth,
    height: height !== undefined && height > 0 ? height : lastValidHeight,
  });

  const updateCardDimensions = debounce((newWidth: number | undefined, newHeight: number | undefined) => {
    let updatedWidth = newWidth !== undefined && newWidth > 0 ? newWidth : lastValidWidth;
    let updatedHeight = newHeight !== undefined && newHeight > 0 ? newHeight : lastValidHeight;

    setCardDimensions({ width: updatedWidth, height: updatedHeight });

    // Update the last valid dimensions when the new values are non-zero
    if (updatedWidth > 0) {
      setLastValidWidth(updatedWidth);
    }
    if (updatedHeight > 0) {
      setLastValidHeight(updatedHeight);
    }
  }, 100); // adjust debounce time as needed

  useEffect(() => {
    updateCardDimensions(width, height);
    return () => {
      updateCardDimensions.cancel();
    };
  }, [width, height]);

  // console.log("width: ", width, " height: ", height);

  // const updateCardDimensions = debounce((width: number | undefined, height: number | undefined) => {
  //   if (width && height) {
  //     width = width
  //     height = height
  //   }
  //   setCardDimensions({ width, height });
  // }, 100); // adjust debounce time

  // useEffect(() => {
  //   updateCardDimensions(width, height);
  //   return () => {
  //     updateCardDimensions.cancel();
  //   };
  // }, [width, height]);

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const handleEdit = () => {
    console.log("handleEdit");
    if (editMode && title !== name) {
      // Save title here or dispatch an action
    }
    setEditMode(!editMode);
  };
  
  const handleDelete = () => {
    setShowModal(true);
  };

  const handleDeleteConfirm = () => {
    setShowModal(false);
    if (onDelete) {
      onDelete(id, cardType);
    }
  };

  const handleDeleteCancel = () => {
    setShowModal(false);
  };

  const handleOpenSettings = () => {
    if (setSelectedMultiPlot) setSelectedMultiPlot();
    setTimeout(()=> {
      if (openPlotOptionsModal) openPlotOptionsModal();
    }, 0);
  }

  const handleFilterValue = (value: FilterState) => {
    setTimeout(()=> {if (typeof setAxisFiltersState === 'function') setAxisFiltersState(value);}, 0);
  };   

  // Not using this, now saving direction from the CustomFilter component directly to axisFiltersState
  const handleFilterDialogSave = () => {
    // TODO impliment
    // if (setAxisFiltersState !== undefined){

    //   const outAxisConfig: FilterState = {}
    //   let index = 0;
    //   for (let value of filterValue){
    //     const fullAxis = value.axis === 'x' ? 'X-Axis Data' : 'Y-Axis Data'
    //     const split = value.value.split('|')
    //     const versionType = split[0]
    //     const column = split[1]
    //     // const newKey = `${fullAxis}|${index}|${versionType}|${column}`
    //     const valueList = split[2].split(',').map(v=>({value:v, label:v}))
    //     outAxisConfig[index] = {
    //       versionType,
    //       column,
    //       filterBehavior: 'include',
    //       type: 'categorical',
    //       value: valueList,
    //       viewable: value.viewable
    //     }
    //     index += 1;
    //   }
    //   if (typeof setConfigureTab === 'function') setConfigureTab();
    //   setTimeout(()=> {if (typeof setAxisFiltersState === 'function') setAxisFiltersState(outAxisConfig);}, 0); // 0 sec timeout allows inline state changes that depend on the previous
    //   // setTimeout(()=> {if (typeof updateFilters === 'function') updateFilters();}, 0)
    // }
  }

  const handleOpenFilterModal = () => {
    if (setSelectedMultiPlot) setSelectedMultiPlot();
    // if (typeof setConfigureTab === 'function') setConfigureTab();
    setTimeout(()=> {
      setCustomFilterModalOpen(true);
    }, 0);
  }

  const cardStyle = {
    width: width,
    height: height,
    text_align: 'center',
    border: selected ? '2px solid #11B2F3' : undefined
  };

  const clickDashModeBarButton = (plotID: number, dataTitle: string): void => {

    // Select the plot container by its unique identifier
    const plotContainer = document.querySelector(`[data-plot-id='${plotID}']`);

    if (!plotContainer) {
      console.error(`No plot found with ID: ${plotID}`);
      return;
    }

    // Find the modebar button within this plot's container
    const button = plotContainer.querySelector(`[data-title='${dataTitle}']`) as HTMLElement;

    if (!button) {
      console.warn(`Button with title "${dataTitle}" not found for plot ID: ${plotID}`);
      return;
    }

    // Simulate a click event
    button.click();

    // const drawrectDashComponent = document.querySelector(`[data-title='${dataTitle}']`) as HTMLElement | null;
    // drawrectDashComponent?.click();
  }; 

  const handleSharedYClick = () => {
    if (setTileSharedY) {
      setTileSharedY(!tileSharedY);
    }
  };

  const handleSharedXClick = () => {
    if (setTileSharedX) {
      setTileSharedX(!tileSharedX);
    }
  };

  const handleHeatmapSwitch = () => {

    // console.log("axisFilterState: ", axisFiltersState);

    const currentSelectedItems = structuredClone(selectedItems);
    if (currentSelectedItems === undefined){
      console.log("No selected items")
      return; 
    }

    // First save state
    setInitialConfig(plotConfig);
    // Set last plot type
    setLastPlotType('heatmap');
    // Save state of selectedItems
    setLastSelectedItems(selectedItems);

    const xAxisData = currentSelectedItems['X-Axis Data']?.[0];
    const yAxisData = currentSelectedItems['Y-Axis Data']?.[0];

    if (yAxisData) {

      // Update Z-Axis Data with the first Y-Axis Data item
      currentSelectedItems['Z-Axis Data'][0] = yAxisData;

      // Now adjust Group by -- ensure groupBy at a minimum includes X-Axis and Y-Axis categorical data, as well as Sample Title
      const zGroupBy = new Set(currentSelectedItems['Z-Axis Data'][0].groupBy);
      zGroupBy.add('Sample Title');
      zGroupBy.add(xAxisData.name);
      currentSelectedItems['Z-Axis Data'][0].groupBy = Array.from(zGroupBy);
  
      if (axisFiltersState && Object.keys(axisFiltersState).length !== 0 && axisFiltersState?.["0"]?.column) {
        // If there is a filter, default setting the yAxis to this filter setting
        currentSelectedItems['Y-Axis Data'][0] = {...yAxisData, method: 'categorical', values: [axisFiltersState?.["0"]?.column]};
      } else {
        // Set Y-Axis to Sample Title
        currentSelectedItems['Y-Axis Data'][0] = {
          name: "Sample Title",
          versionType: null,
          method: "index",
          values: ["Sample Title"],
          pipelines: [],
        };
      } 
      
      // Update state with modified selected items
      
      console.log("Switching to heatmap");
      handleChange({ plotType: 'heatmap', ymin: null, ymax: null, pvalue: undefined }, );
      // setTimeout(()=> setItems?.(currentSelectedItems), 0);
    } else {
      console.log("Y-Axis Data is empty, no changes applied.");
    }
  };

  const handleBarPlotSwitch = () => {

    // Check if we are reverting from a heatmap plot
    handleChange({plotType: 'bar', ymin: initialConfig?.ymin ?? null, ymax: initialConfig?.ymax ?? null, pvalue: initialConfig?.pvalue});
    if (lastPlotType === 'heatmap' && selectedItems) {
      // console.log("Reverting to initial configuration from heatmap plot.");
      // Update the plot configurations with the initial configuration
      // setPlotConfigs?.({ plot1: initialConfig }, false);

      // setItems?.(newSelectedItems);
      // setTimeout(() => setItems?.(newSelectedItems), 10);
    }

  }

  const handleBoxPlotSwitch = () => {

    // Check if we are reverting from a heatmap plot
      handleChange({plotType: 'box', ymin: initialConfig?.ymin ?? null, ymax: initialConfig?.ymax ?? null, pvalue: initialConfig?.pvalue});

    if (initialConfig && lastPlotType === 'heatmap' && lastSelectedItems) {
      // console.log("Reverting to initial configuration from heatmap plot.");
      // Update the plot configurations with the initial configuration
      // setPlotConfigs?.({ plot1: initialConfig }, false);
      // setItems?.(lastSelectedItems);
    }

    // handleChange({plotType: 'box'});
  };

  const handleScatterPlotSwitch = () => {
    // 

    // Check if we are reverting from a heatmap plot
    // if (initialConfig && lastPlotType === 'heatmap' && lastSelectedItems) {
      // console.log("Reverting to initial configuration from heatmap plot.");
      // Update the plot configurations with the initial configuration
      // setPlotConfigs?.({ plot1: initialConfig }, false);
      // setItems?.(lastSelectedItems);
    // }

    handleChange({plotType: 'scatter'});
  };

  const handleYcluster = () => {
    handleChange({heatmapYcluster: plotConfig?.heatmapYcluster ? false : true})
  };

  const handleLog2transform = () => {
    const zAxisKey = selectedItems?.['Z-Axis Data']?.[0]?.values[0];
    const newColorScaleTitle = plotConfig?.heatmapLog2 ? zAxisKey : `Log2(${zAxisKey})`;
    handleChange({heatmapLog2: plotConfig?.heatmapLog2 ? false : true, colorscaleTitle: newColorScaleTitle});
  };

  const handleChange = (changes: Partial<PlotConfig>) => {
      if (setPlotConfigs) {
        const currentConfig = structuredClone(plotConfig);
        
        // const currentConfig = plotConfig || {}; 
        const updatedConfig = { ...currentConfig, ...changes } as PlotConfig;
  
        setPlotConfigs({ plot1: updatedConfig }, false);
      } else {
        console.error("setPlotConfigs is undefined.");
      }
  };

  const handleClickDiffControlSelectionMenu = (control: string) => {
    // console.log("setting selectedDiffControlSection to: ", control);
    setSelectedDiffControlSelection(control); // persist selection
    
    // Now set the new reference control
    const currentSelectedItems = structuredClone(selectedItems);
    const axisRef = plotConfig?.plotType === 'heatmap' ? 'Z-Axis Data' : 'Y-Axis Data';
    const item = currentSelectedItems?.[axisRef]?.[0];
    if (item) {
      // set control if it is defined and there is only one reference condition
      if (control !== undefined && item.references?.length === 1) {
        item.references[0].value.value = control;
      }
    }
    if (currentSelectedItems) setItems?.(currentSelectedItems);
    
    handleCloseDiffControlSelectionMenu();
  };

  const handleDifferentialModeChange = () => {
    
    const axisRef = plotConfig?.plotType === 'heatmap' ? 'Z-Axis Data' : 'Y-Axis Data';
    const axisData = selectedItems?.[axisRef]?.[0];

    const currentSelectedItems = structuredClone(selectedItems);

    // const currentSelectedItems = { ...selectedItems }; 
    if (currentSelectedItems === undefined){
      console.log("No selected items")
      return; 
    }
    
    if (!axisData?.processingMode) {
      console.log("No processing mode"); 
      return;
    }
  
    if (axisData.processingMode === 'relative') {
      
      // Switch to absolute mode
      // axisData.processingMode = 'absolute';
      currentSelectedItems[axisRef][0].processingMode = 'absolute' 
      setItems?.(currentSelectedItems);
      setDifferentialModeEnabled(false);

      // // Revert to original settings
      // if (initialConfig !== undefined) {
      //   setPlotConfigs?.({ plot1: initialConfig }, false);
      // }

      // handleChange({...plotConfig, colorscaleTitle: undefined, ytitle: initialConfig?.ytitle, plotType: initialConfig?.plotType, pvalue: initialConfig?.pvalue});

      // Turn off shared Y
      // setTimeout(()=> setTileSharedY?.(false), 0); // Settimeout of 0 to Prevent race condition with selectedItems and plotConfig updates

    } else {

      // First save state
      setInitialConfig(plotConfig);

      const item = currentSelectedItems[axisRef]?.[0];

      if (item) {
        // switch to relative mode
        item.processingMode = 'relative';

        // Check if reference condition is set and valid
        if (item.references && item.references.length > 0 && item.references[0].column.value === 'Treatment_and_Dose') {
          if (!categoricalColumns['Treatment_and_Dose']?.includes(item.references[0].value.value)) {
            item.references[0].value.value = categoricalColumns['Treatment_and_Dose']?.[0];
            setSelectedDiffControlSelection(item.references[0].value.value); // persist selection
          }
        }
      }

      setItems?.(currentSelectedItems);
      setDifferentialModeEnabled(true);

      // const current_title = cardProps?.plotConfig?.title || '';
      const relative_units = selectedItems?.[axisRef]?.[0]?.relativeUnits || '';

      const controlTitle = Array.isArray(selectedItems?.[axisRef]?.[0]?.references)
          ? (selectedItems?.[axisRef]?.[0]?.references || [])
              .map(ref => ref?.value?.value || '') 
              .filter(Boolean)
              .join(', ')
              .replace("Media: 0 µg/mL", "Media")
          : '';

      const relative_title_map: Record<string, string> = {
        percentage_delta: "% Delta", 
        fold_change: "Fold Change",
        log2_fold_change: "Log2(FC)",
        log2_fold_change_smooth: "Log2(FC) Smooth",
        delta: "Delta",
        percentage: "%",
      }

      const relative_title = relative_title_map[relative_units] || '[Unknown Unit]'; // Fallback for unmapped units
      const new_title = `${relative_title} with ${controlTitle}`;

      const short_new_title = new_title.length < 10 ? new_title : new_title.slice(0,10) + "...";

      
      // Change plot config to auto scale range, define title, plot type, and turn off statistics 
      // TO DO: if gene box plot mode, don't need to change to bar plot

      // if (plotConfig?.plotType !== 'heatmap') {
      //   handleChange({ytitle: new_title, plotType: 'bar', pvalue: undefined, pvalueDisplayMode: 'text'});
      //   // Turn on shared y setting
      //   setTimeout(()=> setTileSharedY?.(true), 0); // Settimeout of 0 to Prevent race condition with selectedItems and plotConfig updates
      // } else {
      //   handleChange({ytitle: undefined, colorscaleTitle: new_title, pvalue: undefined, pvalueDisplayMode: 'text'});
      // }
    }

  };

  const handleStatisticsClick = () => {
    if (plotConfig?.pvalue === undefined) {
      handleChange({pvalue: 'control', pvalueDisplayMode: plotConfig?.pvalueDisplayMode ? plotConfig?.pvalueDisplayMode : 'text'})
    } else {
      handleChange({pvalue: undefined});
    }
  };
  
  const handleDataSelectionMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {  
    setAnchorDataSelectionMenu(event.currentTarget);
  };

  const handleCloseDataSelectionMenu = () => {
    setAnchorDataSelectionMenu(null);
  };

  const handleClickDataSelectionMenu = (option: string, pipeline: number | null, plotType: string | undefined) => {
    
    
    setSelectedDataSelection(option); // persist selection

    // update selectedItems y data value using setItems
    const currentSelectedItems = structuredClone(selectedItems);
    if (currentSelectedItems === undefined){
      console.log("No selected items")
      return; 
    }

    if (plotType === 'heatmap') {
      // console.log("Detected heatmap plot; setting Z-Axis Data");
      const zAxisData = currentSelectedItems['Z-Axis Data']?.[0];
      // set zAxisData values to the selected option
      if (zAxisData) {
        zAxisData.values[0] = option;
        if (pipeline !== null) {
          zAxisData.pipelines = pipeline ? [pipeline] : [];
          setDataPipeline?.(pipeline);
        }
        setItems?.(currentSelectedItems);
      } else {
        console.log("No Z-Axis Data found");
      }
      // console.log("Updated selected items: ", currentSelectedItems);
    } else { // assume y axis data
      console.log("Detected non-heatmap plot; setting Y-Axis Data");
      const yAxisData = currentSelectedItems['Y-Axis Data']?.[0];
      // set yAxisData values to the selected option
      if (yAxisData) {
        yAxisData.values[0] = option;
        if (pipeline !== null) {
          yAxisData.pipelines = pipeline ? [pipeline] : [];
          setDataPipeline?.(pipeline);
        }
        setItems?.(currentSelectedItems);
      } else {
        console.log("No Y-Axis Data found");
      }
      // console.log("Updated selected items: ", currentSelectedItems);
    }
    handleCloseDataSelectionMenu();
  };

  const handleControlSelectionMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorControlSelectionMenu(event.currentTarget);
  };

  const handleCloseControlSelectionMenu = () => {
    setAnchorControlSelectionMenu(null);
  };

  const handleClickControlSelectionMenu = (control: string) => {
    console.log("setting selectedControlSection to: ", control);
    setSelectedControlSelection(control); // persist selection
    handleChange({pvalueControl: control});
    handleCloseControlSelectionMenu();
  };

  
  const handleDiffControlSelectionMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorDiffControlSelectionMenu(event.currentTarget);
  };

  const handleCloseDiffControlSelectionMenu = () => {
    setAnchorDiffControlSelectionMenu(null);
  };

  // const debouncedChange = useCallback(
  //   _.debounce((key: keyof PlotConfig, value: any) => handleChange(key, value), 300),
  //   [handleChange]
  // );

  return (
    <React.StrictMode>
      <div ref={ref} style={{ display: 'flex', height: '100%', width:'100%' }}>
        {alerts ? alerts : []}
        <Card style={cardStyle} className={`${className} cardHover`}>
        {(edit || filter || deletable || refresh) 
          && <CardHeader 
                sx={{
                  display: 'flex',
                  alignItems: 'center', 
                  // justifyContent: 'space-between',
                  width: "100%",
                  padding: 0, 
                  margin: 0,
                }}
                titleTypographyProps={{ align: 'center', variant: 'h6'}}
                // className = "drag-handle custom-card-header"
                className = "custom-card-header"
                action={
                <CardActions sx={{flexGrow: 1, marginRight: "10px", display: 'flex', flexDirection: 'row', width: '100%'}}> 
                    
                  {/* Old Original Configurable Buttons */}
                  {filter && <IconButton aria-label="refresh" size="small">
                      <FilterAltOutlinedIcon />
                  </IconButton>}
                  {deletable && <IconButton aria-label="delete" onClick={handleDelete} size="small">
                      <DeleteOutlineIcon />
                  </IconButton>}
                  
                  {/* <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}> */}
                    <Collapse in={showOptions} timeout="auto" unmountOnExit orientation="horizontal">
                      <Box
                        sx={{
                          display: 'flex',
                          // justifyContent: 'space-end',
                          gap: 2,
                          width: "100%"
                        }}
                      >
                        { isGeneExpressionData &&
                          <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', width: '100%' }}>
                            <Tooltip title="Data Selection">
                              <IconButton
                                id="data-selection-button"
                                aria-label="data selection"
                                aria-controls={openDataSelection ? 'dataselection-menu' : undefined}
                                aria-haspopup="true"
                                aria-expanded={openDataSelection ? 'true' : undefined}
                                size="small"
                                sx={{ padding: '5px' }}
                                onClick={handleDataSelectionMenuClick}
                              >
                                <TuneIcon
                                  sx={{ fontSize: '20px', color: 'lightgrey' }}
                                />
                              </IconButton>
                            </Tooltip>
                            <Menu
                              id="dataselection-menu"
                              anchorEl={anchorDataSelectionMenu}
                              open={openDataSelection}
                              onClose={handleCloseDataSelectionMenu}
                              MenuListProps={{
                                'aria-labelledby': 'data-selection-button',
                              }}
                            >
                              {geneExpressionDataSelectionOptions.map((option) => (
                                <MenuItem
                                  key={option.value}
                                  selected={option.value === selectedDataSelection}
                                  onClick={() => handleClickDataSelectionMenu(option.value, option.pipeline, plotConfig?.plotType)}
                                  sx={{
                                    minHeight: '32px',          // reduce row height
                                    fontSize: '0.8rem',         // smaller font
                                    py: 0.5,                    // less vertical padding
                                  }}
                                >
                                  <ListItemIcon sx={{ minWidth: '30px' }}>
                                    <Radio
                                      checked={option.value === selectedDataSelection}
                                      value={option.value}
                                      size="small"
                                      sx={{
                                        p: 0.25,
                                        '& .MuiSvgIcon-root': {
                                          fontSize: 16, // reduce radio circle size
                                        },
                                      }}
                                    />
                                  </ListItemIcon>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Menu>
                          </Box>
                        }
                        
                        {/* Plot Specific Options - Align to the Left */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}> 
                          {onAddColumn && 
                            <Tooltip title="Add Tile Column">
                              <span> {/* Span is needed to provide a wrapping element for the IconButton when disabled */}
                                <IconButton
                                  aria-label="add column"
                                  size="small"
                                  onClick={onAddColumn}
                                  disabled={!onAddColumn}
                                >
                                  <AddColumnIcon sx={{color: 'lightgrey'}} disabled={!onAddColumn}/>
                                </IconButton>
                              </span>
                            </Tooltip>
                          }

                          {onRemoveColumn && 
                            <Tooltip title="Remove Tile Column">
                              <span> {/* Span is needed to provide a wrapping element for the IconButton when disabled */}
                                <IconButton
                                  aria-label="remove column"
                                  size="small"
                                  onClick={onRemoveColumn}
                                  sx={{ padding: '5px' }}
                                  // Disable button if function is not provided or if numColumns is 0
                                  disabled={!onRemoveColumn || numColumns === 1}
                                >
                                  <RemoveColumnIcon sx={{ color: 'lightgrey'}} disabled={!numColumns || numColumns === 1}/>
                                </IconButton>
                              </span>
                            </Tooltip>
                          }
                          {tileModeOn && 
                            <Tooltip title="Shared X Axis">
                              <span> {/* Span is needed to provide a wrapping element for the IconButton when disabled */}
                                <IconButton
                                  aria-label="shared x axis"
                                  size="small"
                                  sx={{ padding: '5px' }}
                                  // Disable button if function is not provided or if numColumns is 0
                                  disabled={!tileModeOn}
                                  onClick={handleSharedXClick}
                                >
                                  <XAxisIcon enabled={tileSharedX}/>
                                </IconButton>
                              </span>
                            </Tooltip>
                          }
                          {tileModeOn && 
                            <Tooltip title="Shared Y Axis">
                              <span> {/* Span is needed to provide a wrapping element for the IconButton when disabled */}
                                <IconButton
                                  aria-label="shared y axis"
                                  size="small"
                                  sx={{ padding: '5px' }}
                                  // Disable button if function is not provided or if numColumns is 0
                                  disabled={!tileModeOn}
                                  onClick={handleSharedYClick}
                                >
                                  <YAxisIcon enabled={tileSharedY}/>
                                </IconButton>
                              </span>
                            </Tooltip>
                          }

                          {(plotConfig?.plotType === 'heatmap') && 
                            <Tooltip title="Log2 Transform">
                              <span> {/* Span is needed to provide a wrapping element for the IconButton when disabled */}
                                  <IconButton
                                    aria-label="log 2 transform"
                                    size="small"
                                    sx={{ padding: '5px' }}
                                    onClick={handleLog2transform}
                                  >
                                    <Log2Icon
                                      enabled={plotConfig?.heatmapLog2}
                                    />
                                  </IconButton>
                                </span>
                            </Tooltip>
                          }   
                          
                          {(plotConfig?.plotType === 'heatmap') && 
                            <Tooltip title="Cluster Y">
                              <span> {/* Span is needed to provide a wrapping element for the IconButton when disabled */}
                                  <IconButton
                                    aria-label="cluster y"
                                    size="small"
                                    sx={{ padding: '5px' }}
                                    // Disable button if function is not provided or if numColumns is 0
                                    // disabled={!tileModeOn}
                                    onClick={handleYcluster}
                                  >
                                    <BubbleChartOutlinedIcon
                                      sx={{color: plotConfig?.heatmapYcluster ? ACTIVE_BLUE : INACTIVE_GREY}}
                                    />
                                  </IconButton>
                                </span>
                            </Tooltip>
                          }
                          
                          {hasReferences && 
                            <>
                              <Tooltip title="Differential Mode">
                                <span> {/* Span is needed to provide a wrapping element for the IconButton when disabled */}
                                    <IconButton
                                      aria-label="differential mode"
                                      size="small"
                                      sx={{ padding: '5px' }}
                                      // Disable button if function is not provided or if numColumns is 0
                                      // disabled={!tileModeOn}
                                      onClick={handleDifferentialModeChange}
                                    >
                                      <DeltaIcon 
                                        sx={{ fontSize: 20 }}
                                        // enabled={(plotConfig?.plotType !== 'heatmap') ? (selectedItems?.['Y-Axis Data']?.[0]?.processingMode === 'relative') :
                                        //         (selectedItems?.['Z-Axis Data']?.[0]?.processingMode === 'relative')}
                                        enabled={differentialModeEnabled}
                                      />
                                    </IconButton>
                                  </span>
                              </Tooltip>
                              { differentialModeEnabled && diffModeREFenabled &&  (
                                <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', width: '100%' }}>
                                    <Tooltip title="Differential Reference Selection">
                                      <IconButton
                                        id="diff-control-selection-button"
                                        aria-label="diff-control selection"
                                        aria-controls={openDiffControlSelection ? 'diff-controlselection-menu' : undefined}
                                        aria-haspopup="true"
                                        aria-expanded={openDiffControlSelection ? 'true' : undefined}
                                        size="small"
                                        sx={{ padding: '5px' }}
                                        onClick={handleDiffControlSelectionMenuClick}
                                      >
                                        <Box
                                          component="span"
                                          sx={{
                                            fontSize: '12px',
                                            fontWeight: 'bold',
                                            color: 'lightgrey',
                                            fontFamily: 'monospace',
                                            letterSpacing: '1px'
                                          }}
                                        >
                                          REF
                                        </Box>
                                      </IconButton>
                                    </Tooltip>
                                    <Menu
                                      id="diffcontrolselection-menu"
                                      anchorEl={anchorDiffControlSelectionMenu}
                                      open={openDiffControlSelection}
                                      onClose={handleCloseDiffControlSelectionMenu}
                                      MenuListProps={{
                                        'aria-labelledby': 'diff-control-selection-button',
                                      }}
                                    >
                                      {diffReferenceOptions?.map((option) => (
                                        <MenuItem
                                          key={option}
                                          selected={option === selectedDiffControlSelection}
                                          onClick={() => handleClickDiffControlSelectionMenu(option)}
                                          sx={{
                                            minHeight: '32px',          // reduce row height
                                            fontSize: '0.8rem',         // smaller font
                                            py: 0.5,                    // less vertical padding
                                          }}
                                        >
                                          <ListItemIcon sx={{ minWidth: '30px' }}>
                                            <Radio
                                              checked={option === selectedDiffControlSelection}
                                              value={option}
                                              size="small"
                                              sx={{
                                                p: 0.25,
                                                '& .MuiSvgIcon-root': {
                                                  fontSize: 16, // reduce radio circle size
                                                },
                                              }}
                                            />
                                          </ListItemIcon>
                                          {option}
                                        </MenuItem>
                                      ))}
                                    </Menu>
                                </Box>
                              )}
                            </>
                          }
                        </Box>

                        {/* Plot Types - Align to the Center */}
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <Tooltip title="Box Plot">
                            <IconButton
                                aria-label="box plot"
                                size="small"
                                onClick={handleBoxPlotSwitch}
                            >
                              <BoxAndWhiskerIcon
                                enabled={plotConfig?.plotType === 'box'}
                              />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Bar Plot">
                            <IconButton
                                aria-label="bar plot"
                                size="small"
                                onClick={handleBarPlotSwitch}
                            >
                              <BarChartIcon
                                sx={{color:  (plotConfig?.plotType === 'bar') ? ACTIVE_BLUE : INACTIVE_GREY, fontSize: 20}}
                              />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Heatmap">
                            <IconButton
                                aria-label="heatmap"
                                size="small"
                                onClick={() => handleHeatmapSwitch()}
                            >
                              <GridOnIcon
                                sx={{color:  (plotConfig?.plotType === 'heatmap') ? ACTIVE_BLUE : INACTIVE_GREY, fontSize: 20}}
                              />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Scatter Plot">
                            <IconButton
                                aria-label="scatter plot"
                                size="small"
                                onClick={handleScatterPlotSwitch}
                            >
                              <ScatterPlotIcon
                                sx={{color:  (plotConfig?.plotType === 'scatter') ? ACTIVE_BLUE : INACTIVE_GREY, fontSize: 20}}
                              />
                            </IconButton>
                          </Tooltip>
                        </Box>

                        {/* General Plot Control Options - Align to the Right */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Tooltip title="Enable Statistics">
                            <IconButton
                              aria-label="enable statistics"
                              size="small"
                              // sx={{ padding: '5px' }}
                              onClick={handleStatisticsClick}
                            >
                              <InsightsIcon
                                sx={{color: plotConfig?.pvalue === 'control' ? ACTIVE_BLUE : INACTIVE_GREY, fontSize: 20}}
                              />
                            </IconButton>
                          </Tooltip>
                          
                          {/* Menu for selecting pvalue control */}
                          { (plotConfig?.pvalue !== undefined) &&
                              Array.isArray(diffReferenceOptions) &&
                              (diffReferenceOptions.length > 0) && pvalueCTRLenabled && 
                            (
                              <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', width: '100%' }}>
                                <Tooltip title="P-Value Control Selection">
                                  <IconButton
                                    id="control-selection-button"
                                    aria-label="control selection"
                                    aria-controls={openControlSelection ? 'controlselection-menu' : undefined}
                                    aria-haspopup="true"
                                    aria-expanded={openControlSelection ? 'true' : undefined}
                                    size="small"
                                    sx={{ padding: '5px' }}
                                    onClick={handleControlSelectionMenuClick}
                                  >
                                    {/* <TuneIcon
                                      sx={{ fontSize: '20px', color: 'lightgrey' }}
                                    /> */}
                                    <Box
                                      component="span"
                                      sx={{
                                        fontSize: '12px',
                                        fontWeight: 'bold',
                                        color: 'lightgrey',
                                        fontFamily: 'monospace',
                                        letterSpacing: '1px'
                                      }}
                                    >
                                      CTRL
                                    </Box>
                                  </IconButton>
                                </Tooltip>
                                <Menu
                                  id="controlselection-menu"
                                  anchorEl={anchorControlSelectionMenu}
                                  open={openControlSelection}
                                  onClose={handleCloseControlSelectionMenu}
                                  MenuListProps={{
                                    'aria-labelledby': 'control-selection-button',
                                  }}
                                >
                                  {diffReferenceOptions?.map((option) => (
                                    <MenuItem
                                      key={option}
                                      selected={option === selectedControlSelection}
                                      onClick={() => handleClickControlSelectionMenu(option)}
                                      sx={{
                                        minHeight: '32px',          // reduce row height
                                        fontSize: '0.8rem',         // smaller font
                                        py: 0.5,                    // less vertical padding
                                      }}
                                    >
                                      <ListItemIcon sx={{ minWidth: '30px' }}>
                                        <Radio
                                          checked={option === selectedControlSelection}
                                          value={option}
                                          size="small"
                                          sx={{
                                            p: 0.25,
                                            '& .MuiSvgIcon-root': {
                                              fontSize: 16, // reduce radio circle size
                                            },
                                          }}
                                        />
                                      </ListItemIcon>
                                      {option}
                                    </MenuItem>
                                  ))}
                                </Menu>
                              </Box>
                            )
                          }
                      
                          {edit && 
                            <Tooltip title="Filter Plot">
                              <IconButton 
                                aria-label="Filter" 
                                size="medium" 
                                onClick={handleOpenFilterModal}
                              >
                                <FilterAltIcon sx={{ fontSize: '20px', color: 'lightgrey' }}/> 
                              </IconButton>
                            </Tooltip>
                          }
                          <Tooltip title="Download plot as a png" placement="left">
                            <IconButton 
                              // data-title="Download plot as a png"
                              onClick={() => clickDashModeBarButton(id, 'Download plot as a png')}
                              sx={{color: INACTIVE_GREY }}
                            >
                              <CameraAltOutlinedIcon sx={{fontSize: 25, marginTop: "1px"}}/>
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Drag to Arrange Content">
                            <IconButton
                                aria-label="drag to arrange content"
                                size="small"
                                // sx={{ padding: '5px' }}
                            >
                              <MoveCursorIcon
                                className="drag-handle" sx={{color: 'lightgrey', cursor: 'move', fontSize: 20}}
                              />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Download Data">
                            <IconButton 
                              aria-label="Filter" 
                              size="medium" 
                              onClick={handleDownloadData}
                            >
                              <CloudDownloadIcon sx={{ fontSize: '24px', color: 'lightgrey' }}/> 
                            </IconButton>
                          </Tooltip>
                          {edit && 
                            <Tooltip title="Modify Plot Settings">
                              <IconButton 
                                aria-label="edit" 
                                size="medium" 
                                onClick={handleOpenSettings}
                              >
                                <SettingsIcon sx={{ fontSize: '20px', color: 'lightgrey' }}/> 
                              </IconButton>
                            </Tooltip>
                          }
                        </Box>
                      </Box>
                    </Collapse>

                    {/* Expand/Collapse Button - Always visible, aligned to the right */}
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Tooltip title={showOptions ? 'Hide options' : 'Show options'}>
                        <IconButton
                          aria-label="show-options" 
                          onClick={() => setShowOptions(prev => !prev)}
                          size="medium"
                          // sx={{ mr: 1, mt: 1 }}
                          sx = {{ margin: 0.5}}
                        >
                          <MoreHorizIcon sx={{ fontSize: '20px', color: 'lightgrey' }}/>
                        </IconButton>
                      </Tooltip>
                    </Box>

                  {/* </Box> */}

                </CardActions>
                }
              />
        }
        <CardContent 
          onClick={onClick}
          style={{
            padding: 0,
            margin: 0,
          }}
          // sx={{
          //   padding: 0,
          //   margin: 0,
          //   // display: 'flex',
          //   // justifyContent: 'center',
          //   // alignItems: 'center', 
          // }}
        >
        {isLoading ? 
        <Box sx={{ display: 'flex', overflow: 'hidden', justifyContent: 'center', alignItems: 'center', width: cardDimensions.width, height: cardDimensions.height }}>
        {/* // <div> */}
          <CircularProgressWithLabel
            size={'10rem'}
            variant={loadingProgress && loadingProgress < 100 ? "determinate" : "indeterminate"}
            value={loadingProgress && loadingProgress < 100 ? loadingProgress : undefined}
            message={message} 
          />
        {/* // </div> */}
        </Box>
        
        : children({ 
            width: cardDimensions.width || (initialWidth? initialWidth: lastValidWidth), // if width is undefined, use 0 as default
            height: cardDimensions.height || (initialHeight? initialHeight: lastValidHeight), // if height is undefined, use 0 as default
            // width: width !== undefined ? width : lastValidWidth,
            // height: height !== undefined ? height: lastValidHeight,
            editMode: editMode,
        })}
        </CardContent>
        <Modal
            open={showModal}
            onClose={handleDeleteCancel}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
          >
          <Box sx={style}>
              <div>Are you sure you want to delete this?</div>
              <Button onClick={handleDeleteConfirm}>Yes</Button>
              <Button onClick={handleDeleteCancel}>No</Button>
          </Box>
        </Modal>
        <CustomFilterModal
                open={customFilterModalOpen}
                onClose={() => setCustomFilterModalOpen(false)}
                // onSave={() => {handleFilterDialogSave(); setCustomFilterModalOpen(false); updateFilters !== undefined ? updateFilters() : void(0);}}
                setFilterValues={handleFilterValue}
                categoricalColumns={categoricalColumns}
                numericColumns={numericColumns}
                selectedItems={selectedItems}
                axisFilterState={axisFiltersState ?? {}}
                // saveAxis
            />
        </Card>
        </div>
    </React.StrictMode>
  );
};

export default ResponsiveCard;
